import type { components } from "@dolfin/api-admin";

type CompanyInboxBillResponse = components["schemas"]["CompanyInboxBillResponse"];
type Address = components["schemas"]["Address"];

/** The delivery address as lines, not a clamped sentence, so the postcode isn't cut off. Prefers the parsed
 * deliveryAddressDetail; otherwise splits the raw string on its own separators. */
export function DeliveryAddressBlock({ bill }: { bill: CompanyInboxBillResponse }) {
  const detail = bill.deliveryAddressDetail as Address | null | undefined;
  const structured = detail && !detail.isEmpty;

  const lines = structured
    ? [detail.line1, detail.line2, [detail.city, detail.state].filter(Boolean).join(", ")]
        .map((l) => l?.trim())
        .filter((l): l is string => Boolean(l))
    : (bill.deliveryAddress ?? "")
        .split(/\s*[,\n]\s*/)
        .map((l) => l.trim())
        .filter(Boolean);

  // Emphasised separately: on a bill with no other usable line the postcode alone can route.
  const postalCode = structured ? detail.postalCode?.trim() || null : null;
  const country = structured ? detail.country?.trim() || null : null;

  if (lines.length === 0 && !postalCode) {
    return <span className="text-sm text-muted-foreground">Not on the bill</span>;
  }

  return (
    <div className="flex flex-col text-sm leading-snug text-muted-foreground">
      {lines.map((line, i) => (
        <span key={`${line}-${i}`}>{line}</span>
      ))}
      {postalCode && (
        <span className="font-medium text-foreground">
          {postalCode}
          {country ? <span className="ml-1.5 font-normal text-muted-foreground">{country}</span> : null}
        </span>
      )}
    </div>
  );
}
